/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useState } from 'react';
import { Avatar, makeStyles } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  avatar: {
    width: 22,
    height: 22,
    fontSize: '0.7rem',
    fontWeight: 600,
    color: theme.palette.primary.contrastText,
  },
}));

// Deterministic 2-letter initials from a team slug. "m42-online" -> "MO",
// "release-engineers" -> "RE", "ocp" -> "OC". Trims leading "m42-" prefix when
// the rest of the slug carries the team identity so the avatar isn't always "M…".
const initialsFor = (slug: string): string => {
  const cleaned = slug.replace(/^m42-/, '');
  const parts = (cleaned || slug).split(/[-_/]/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  const word = parts[0] ?? slug;
  return (word.slice(0, 2) || '?').toUpperCase();
};

// Deterministic background color from the slug -- not random, so the same team
// always renders the same color. HSL hue 0-360 from a tiny hash.
const colorFor = (slug: string): string => {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360}, 55%, 42%)`;
};

/**
 * Square-ish team avatar. Renders the GitHub team avatar URL when present
 * (set by github-org as `spec.profile.picture`); falls back to a deterministic
 * initials-on-color tile when the URL is missing OR fails to load. The fallback
 * matches GitHub's team avatar dimensions so the chip layout doesn't reflow.
 */
export const TeamAvatar = ({
  slug,
  pictureUrl,
  size = 22,
}: {
  slug: string;
  pictureUrl?: string;
  size?: number;
}) => {
  const classes = useStyles();
  const [failed, setFailed] = useState(false);
  const showImage = !!pictureUrl && !failed;
  return (
    <Avatar
      className={classes.avatar}
      style={{
        width: size,
        height: size,
        backgroundColor: showImage ? undefined : colorFor(slug),
      }}
      src={showImage ? pictureUrl : undefined}
      imgProps={{ onError: () => setFailed(true), loading: 'lazy' }}
      alt=""  // decorative; the team name is alongside in the chip label
    >
      {showImage ? null : initialsFor(slug)}
    </Avatar>
  );
};
